/**
 * whisperSync.ts — Fetches the remote whisper corpus from Supabase and merges
 * it into the in-memory whisper pool.
 *
 * Offline-first: the seed bundle is always available. This service enriches it
 * once per day (TTL) so the app has a larger, more targeted pool when online.
 *
 * Architecture:
 *   1. On startup, call syncWhispers() once.
 *   2. syncWhispers() checks localStorage for a fresh cache.
 *   3. Cache hit → calls setWhisperPool() immediately, no network.
 *   4. Cache miss → fetches from Supabase, writes cache, calls setWhisperPool().
 *   5. Any error → logged and ignored; seed pool remains active.
 */
import { getClient } from "@retreats/supabase-adapter";
import { setWhisperPool } from "@retreats/content";
import type { WhisperEntry, WhisperScreen, IntentTag } from "@retreats/content";
import { STORAGE_KEYS } from "../constants";
import { createChildLogger } from "@retreat/shared";

const log = createChildLogger("whisperSync");

const TABLE = "whispers";
const CACHE_KEY = STORAGE_KEYS.WHISPER_CACHE;
const CACHE_TTL_MS = 24 * 60 * 60 * 1000; // 24 hours

type CachePayload = {
  fetchedAt: number;
  entries: WhisperEntry[];
};

// Supabase row — mirrors the migration schema (snake_case).
type WhisperRow = {
  id: string;
  teacher_id: string;
  line: string;
  full_text?: string | null;
  source: string;
  screens: string[];
  intents: string[];
  tradition_gated?: string | null;
};

function rowToEntry(row: WhisperRow): WhisperEntry {
  return {
    id: row.id,
    teacherId: row.teacher_id,
    line: row.line,
    source: row.source,
    screens: row.screens as WhisperScreen[],
    intents: row.intents.slice(0, 3) as IntentTag[],
    ...(row.tradition_gated ? { traditionGated: row.tradition_gated } : {}),
  };
}

function readCache(): WhisperEntry[] | null {
  try {
    const raw = localStorage.getItem(CACHE_KEY);
    if (!raw) return null;
    const payload = JSON.parse(raw) as CachePayload;
    if (Date.now() - payload.fetchedAt > CACHE_TTL_MS) return null;
    return payload.entries;
  } catch {
    return null;
  }
}

function writeCache(entries: WhisperEntry[]): void {
  try {
    const payload: CachePayload = { fetchedAt: Date.now(), entries };
    localStorage.setItem(CACHE_KEY, JSON.stringify(payload));
  } catch {
    // quota exceeded — skip cache write, pool is still updated in-memory
  }
}

/**
 * Fetch the active whisper corpus from Supabase, update the in-memory pool,
 * and write a 24-hour localStorage cache.
 *
 * Safe to call multiple times — cached until TTL expires.
 * Never throws — errors are logged and the seed pool remains active.
 */
export async function syncWhispers(): Promise<void> {
  const cached = readCache();
  if (cached !== null) {
    setWhisperPool(cached);
    log.debug(`whisper pool loaded from cache (${cached.length} entries)`);
    return;
  }

  try {
    const client = getClient();
    const { data, error } = await client
      .from(TABLE)
      .select("id, teacher_id, line, source, screens, intents, tradition_gated")
      .eq("active", true);

    if (error) {
      log.warn("whisperSync: fetch failed", error.message);
      return;
    }

    const entries = (data as WhisperRow[]).map(rowToEntry);
    writeCache(entries);
    setWhisperPool(entries);
    log.debug(`whisper pool updated from Supabase (${entries.length} entries)`);
  } catch (err) {
    log.warn("whisperSync: unexpected error", err);
  }
}
